import React, { memo, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { AgGridReact } from "ag-grid-react";
import PropTypes from "prop-types";

import WPTertiaryButton from "../WPTertiaryButton";
import WPAGridCellRender from "./WPAGridCellRender";

import CircularLoader from "../../common/styles/components/circularLoader";

import { combineClassNames } from "../../utils/appUtils";
import { generateFileName } from "../../utils/Utils";

import "./WPAgGrid.scss";

const WPAgGrid = ({
  gridRef,
  context,
  rowData,
  getRowId,
  columnDefs,
  onGridReady,
  tableHeight,
  rowSelection,
  defaultColDef,
  onRowSelected,
  onRowDoubleClicked,
  onCellValueChanged,
  onSortChanged,
  gridOptions,
  onSelectionChanged,
  getRowClass,
  enableRowHover,
  loading,
  isScrollIntoView,
  exportFileName,
  disclaimerList,
  localeText,
}) => {
  const { t } = useTranslation();
  const tableDefaultColDef = useMemo(
    () => ({
      ...defaultColDef,
      cellRenderer: WPAGridCellRender,
      tooltipComponent: null,
      suppressMovable: true,
      suppressCellFocus: true,
      sortable: false,
    }),
    [defaultColDef, columnDefs],
  );

  // Ref for the grid container DOM node
  const containerRef = useRef(null);

  const CustomLoadingOverlay = () => <CircularLoader />;

  // Show loader or no rows overlay on grid ready and when loading/rowData changes
  const handleGridReady = (params) => {
    if (onGridReady) onGridReady(params);
    // Scroll grid container into view using ref
    if (containerRef.current && isScrollIntoView) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
    if (loading) {
      params.api.showLoadingOverlay();
    } else if (Array.isArray(rowData) && rowData.length === 0) {
      params.api.showNoRowsOverlay();
    } else {
      params.api.hideOverlay();
    }
  };

  useEffect(() => {
    if (gridRef && gridRef.current && gridRef.current.api) {
      if (loading) {
        gridRef.current.api.showLoadingOverlay();
      } else if (Array.isArray(rowData) && rowData.length === 0) {
        gridRef.current.api.showNoRowsOverlay();
      } else {
        gridRef.current.api.hideOverlay();
      }
    }
  }, [loading, rowData, gridRef]);

  const onExportClicked = () => {
    const api = gridRef?.current?.api;

    if (api) {
      const csvFileName = generateFileName(exportFileName);
      const appendContent = disclaimerList.length
        ? [
            [{ data: { value: "" } }],
            [{ data: { value: "" } }],
            ...disclaimerList.map((line) => [{ data: { value: line } }]),
          ]
        : undefined;

      api.exportDataAsCsv({ fileName: csvFileName, appendContent });
    }
  };

  return (
    <>
      {exportFileName && (
        <WPTertiaryButton
          label={t("ValueStrings.exportCSV")}
          onClick={onExportClicked}
        />
      )}
      <div
        ref={containerRef}
        className="ag-theme-alpine wp-ag-grid"
        style={{ height: tableHeight }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          context={context}
          onGridReady={handleGridReady}
          loadingOverlayComponent={CustomLoadingOverlay}
          getRowId={getRowId}
          defaultColDef={tableDefaultColDef}
          rowSelection={rowSelection}
          onRowSelected={onRowSelected}
          onRowDoubleClicked={onRowDoubleClicked}
          onCellValueChanged={onCellValueChanged}
          onSelectionChanged={onSelectionChanged}
          onSortChanged={onSortChanged}
          gridOptions={gridOptions}
          tooltipShowDelay={500}
          tooltipMouseTrack={true}
          getRowClass={(params) =>
            combineClassNames(
              getRowClass && getRowClass(params),
              enableRowHover && "hover-enabled",
            )
          }
          localeText={localeText}
        />
      </div>
    </>
  );
};

WPAgGrid.propTypes = {
  getRowId: PropTypes.func,
  rowData: PropTypes.array,
  gridRef: PropTypes.object,
  context: PropTypes.object,
  columnDefs: PropTypes.array,
  onGridReady: PropTypes.func,
  onRowDragEnd: PropTypes.func,
  onRowSelected: PropTypes.func,
  gridOptions: PropTypes.object,
  tableHeight: PropTypes.string,
  rowSelection: PropTypes.string || PropTypes.object,
  defaultColDef: PropTypes.object,
  onRowDoubleClicked: PropTypes.func,
  onCellValueChanged: PropTypes.func,
  onSelectionChanged: PropTypes.func,
  onSortChanged: PropTypes.func,
  enableRowHover: PropTypes.bool,
  loading: PropTypes.bool,
  isScrollIntoView: PropTypes.bool,
  exportFileName: PropTypes.string,
  disclaimerList: PropTypes.array,
  localeText: PropTypes.object,
};

WPAgGrid.defaultProps = {
  rowData: [],
  columnDefs: [],
  tableHeight: "300px",
  rowSelection: "single",
  enableRowHover: false,
  loading: false,
  isScrollIntoView: false,
  disclaimerList: [],
  localeText: {},
};

export default memo(WPAgGrid);
